import styled from 'styled-components';
import React, { FC } from 'react';
import { useTheme } from '@algoan/deep-dive/theme';
import { Loader } from '@algoan/deep-dive/loader';
import { ComponentProps } from '@algoan/deep-dive/model';

type Variant = 'main' | 'primary' | 'secondary' | 'destructive' | 'algoan';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, ComponentProps {
  /** To choose the button style */
  variant?: Variant;
  /**Allow to display a loading icon in the button component */
  isLoading?: boolean;
}

export const Button: FC<ButtonProps> = ({
  children,
  variant,
  disabled = false,
  isLoading,
  className,
  'data-testid': dataTestId,
  ...buttonAttributes
}) => {
  const theme = useTheme();

  const getColorFromVariant = (): string => {
    switch (variant) {
      case 'secondary':
        return theme.middleGrey;
      case 'destructive':
        return theme.delete;
      default:
        return theme.bluePeps;
    }
  };

  return (
    <S.Container
      color={getColorFromVariant()}
      disabled={disabled || !!isLoading}
      variant={variant}
      isLoading={isLoading}
      className={className}
      data-testid={dataTestId ?? 'button'}
      {...buttonAttributes}
    >
      {isLoading && (
        <S.Loader
          variant={variant}
          color={variant === 'main' || variant === 'algoan' ? theme.white : getColorFromVariant()}
        />
      )}
      {variant === 'algoan' ? (
        <>
          <S.Text isLoading={!!isLoading}>{children}</S.Text>
          <S.Text isLoading={!!isLoading}>{children}</S.Text>
        </>
      ) : (
        <S.ChildContainer isLoading={!!isLoading}>{children}</S.ChildContainer>
      )}
    </S.Container>
  );
};

const Text = styled.span<{ isLoading?: boolean }>`
  display: block;
  margin: 0px auto;
  height: 100%;
  position: relative;
  top: 0%;
  transition: 0.3s;
  user-select: none;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  ${(props) => (props.isLoading ? 'display: none;' : '')}
`;

const S = {
  Container: styled.button<{ disabled: boolean; color: string; variant?: Variant; isLoading?: boolean }>`
    outline: none;
    border: 1px solid ${(props) => props.color};
    box-sizing: border-box;
    border-radius: 40px;
    padding: 0px 20px;
    position: relative;
    font-style: normal;
    font-weight: ${(props) => props.theme.fontWeight.medium};
    font-size: 14px;
    height: 48px;
    text-align: center;
    color: ${(props) => (props.variant === 'main' || props.variant === 'algoan' ? props.theme.white : props.color)};
    background-color: ${(props) =>
      props.variant === 'main' || props.variant === 'algoan' ? props.color : props.theme.white};
    ${(props) => (props.disabled && !props.isLoading ? 'filter: opacity(0.4);' : '')}

    &:hover {
      box-shadow: ${(props) =>
        props.variant === 'main' && !props.disabled ? '0px 4px 20px rgba(0, 0, 0, 0.18)' : 'none'};
    }

    ${(props) =>
      props.disabled
        ? ``
        : `&:hover {
            background-color: ${props.color};
            color: #ffffff;
            cursor: pointer;
          }`}
    ${(props) =>
      props.variant === 'algoan'
        ? `
          display: inline-block;
          height: 64px;
          width: 205px;
          line-height: 64px;
          overflow: hidden;
          transition: 0.3s;
          font-size: 17px;
          padding: 0;



          &:hover ${Text} {
            top: ${props.disabled ? '' : '-100%'};
          }
    `
        : ''};
  `,
  ChildContainer: styled.div<{ isLoading: boolean }>`
    opacity: ${(props) => (props.isLoading ? 0 : 1)};
  `,
  Loader: styled(Loader)<{ variant?: Variant }>`
    width: ${(props) => (props.variant === 'algoan' ? '40px' : '30px')};
    position: absolute;
    bottom: ${(props) => (props.variant === 'algoan' ? '-2px' : '-9px')};
    right: ${(props) => (props.variant === 'algoan' ? 'calc(50% - 20px)' : 'calc(50% - 15px)')};
  `,
  Text,
};

export const ButtonStyle = S;
